import {fsa, fsaError, fsaPayloadByKeys, fsaPayload} from './helpers'
import {createNewEvent} from './events'
import {openLookNamePanel} from './modal'
import {initiateOnboarding} from '../components/Onboarding/redux/actions'
import {openPartyAvailableModal} from './productDetailPage'
import {queueLookbuilderProducts} from './lookbuilder'

import * as cross from '../store/cross'
import * as events from '../store/events'
import * as looks from '../store/looks'
import * as user from '../store/user'

export const ADD_RECOMMENDATION_MODAL_PRODUCT = 'add recommendation modal product to look'
export const addRecommendationModalProduct = fsaPayloadByKeys(ADD_RECOMMENDATION_MODAL_PRODUCT, [
  'products',
])

export const ADD_PRODUCTS_TO_LOOKBUILDER = 'add product(s) to lookbuilder'
export const addProductsToLookbuilder = fsaPayloadByKeys(ADD_PRODUCTS_TO_LOOKBUILDER, ['products'])

export const ADD_PRODUCT_TO_LOOK = 'add product to look'
export const addProductToLook = fsaPayloadByKeys(ADD_PRODUCT_TO_LOOK, ['lookId', 'origin'])

export const ADD_PRODUCTS_TO_EXISTING_LOOK = 'add products to existing look'
export const addProductsToExistingLook = fsaPayload(ADD_PRODUCTS_TO_EXISTING_LOOK)

export const ADD_PRODUCT_TO_HTO_LOOK = 'add product to hto look'
export const addProductToHtoLook = fsa(ADD_PRODUCT_TO_HTO_LOOK)

export const ADD_PRODUCTS_TO_NEW_LOOK = 'add product(s) to new look'
export const addProductsToNewLook = fsaPayloadByKeys(ADD_PRODUCTS_TO_NEW_LOOK, [
  'lookName',
  'products',
  'origin',
])

export const ADD_RECOMMENDED_PRODUCTS_TO_LOOK = 'add recommended products to look'
export const addRecommendedProductsToLook = fsaPayloadByKeys(ADD_RECOMMENDED_PRODUCTS_TO_LOOK, [
  'lookId',
  'origin',
])

export const ADD_LOOKBUILDER_PRODUCTS_TO_LOOK = 'add lookbuilder products to look'
export const addLookbuilderProductsToLook = fsaPayloadByKeys(ADD_LOOKBUILDER_PRODUCTS_TO_LOOK, [
  'products',
])

// Internal Actions

export const HANDLE_ADDING_PRODUCT_TO_HTO_LOOK = 'handle adding product to hto look'
export const handleAddingProductToHtoLook = fsa(HANDLE_ADDING_PRODUCT_TO_HTO_LOOK)

// Flow state actions

export const ADD_TO_LOOK_SUCCESS = 'add to look flow successful'
export const addToLookSuccess = fsa(ADD_TO_LOOK_SUCCESS)

export const ADD_TO_LOOK_PENDING = 'add to look flow pending'
export const addToLookPending = fsa(ADD_TO_LOOK_PENDING)

export const ADD_TO_LOOK_FAILURE = 'add to look flow failed'
export const addToLookFailure = fsaError(ADD_TO_LOOK_FAILURE)

export const ADD_TO_LOOK_IDLE = 'add to look flow idle'
export const addToLookIdle = fsa(ADD_TO_LOOK_IDLE)

// Thunks

/**
 * A "wrapper" thunk that prefaces adding to a look. It ensures that users have
 * a valid current event, are authenticated, that the party is available, and
 * the event is not sold out.
 * @param {Function} dispatch
 * @param {Function} getState
 * @return {Promise}
 */
const validateAddToLook = (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    const state = getState()
    if (!user.isAuthenticated(state)) {
      return dispatch(initiateOnboarding(resolve))
    }
    if (!cross.onboardingIsComplete(state)) {
      // The `initiateOnboarding` action does _not_ handle a case where a user
      // has an account, is logged in, but has no event. We must create a blank
      // event here - `createNewEvent` calls `initiateOnBoarding` on success.
      if (!events.currentEventIsValid(state)) {
        return dispatch(createNewEvent(resolve))
      }
      return dispatch(initiateOnboarding(resolve))
    }
    if (cross.isRentSoldOutForEvent(state) && cross.isBuySoldOutForCurrentDate(state)) {
      return reject() // do nothing; this could open a modal in the future
    }
    if (!cross.isPartyAvailable(state)) {
      return dispatch(openPartyAvailableModal(resolve))
    }
    return resolve()
  })
}

export const addToLook = (lookId, products) => async (dispatch) => {
  dispatch(addToLookPending())
  await dispatch(validateAddToLook)
  dispatch(addProductsToExistingLook({lookId, products}))
}

const validateAddProductsToLookbuilder = (dispatch, getState) => {
  return new Promise((resolve) => {
    const state = getState()
    if (!user.isAuthenticated(state)) {
      return dispatch(initiateOnboarding(resolve))
    }
    return resolve()
  })
}

/**
 * A thunk to fire for adding product(s) to lookbuilder. Initiates onboarding
 * if a user is not authenticated.
 *
 * @param {Object[]} products Array of product slugs
 */
export const handleAddProductsToLookbuilder = (products) => async (dispatch) => {
  await dispatch(validateAddProductsToLookbuilder)
  dispatch(queueLookbuilderProducts(products))
}

/**
 * The thunk to fire for adding products to a new look. Handles look name
 * selection if necessary.
 *
 * @param {Object[]} products Array of product objects to add to a new look
 * @param {String} origin The originator of this cascade used for `track.createdLook`
 */
export const addToNewLook = (products, origin) => async (dispatch, getState) => {
  dispatch(addToLookPending())
  await dispatch(validateAddToLook)

  const state = getState()
  const hasNonHtoLooks = looks.hasNonHtoLooks(state)
  const formattedNewLookName = events.formatNewLookName(state)
  if (hasNonHtoLooks || !formattedNewLookName) {
    dispatch(openLookNamePanel(products, origin))
    return
  }

  dispatch(addProductsToNewLook(formattedNewLookName, products, origin))
}

/**
 * HTO looks can only be created from the `/events/<id>/looks` endpoint, so we
 * need to ensure that those events are created before called `_addProductToLook`.
 * This also takes care of the HTO-specific analytics tracking.
 * @return {Function} The "thunk" function - returns a Promise
 */
export const addToHtoLook = () => async (dispatch) => {
  dispatch(addToLookPending())
  await dispatch(validateAddToLook)
  dispatch(handleAddingProductToHtoLook())
}
